import { Fragment } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  HomeIcon,
  ChartBarIcon,
  ChevronDownIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  WrenchScrewdriverIcon,
  EnvelopeIcon,
  StarIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import { Disclosure, Menu, Transition } from '@headlessui/react';
import { useAuth } from '../../contexts/AuthContext';

const navigation = [
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: HomeIcon,
  },
  {
    name: 'Scores',
    href: '/leagues',
    icon: ChartBarIcon,
  },
  {
    name: 'Picks & Edges',
    href: '/top-picks',
    icon: StarIcon,
    premium: true,
  },
  {
    name: 'Parlay Workshop',
    href: '/parlay-workshop',
    icon: WrenchScrewdriverIcon,
    premium: true,
  },
  // Bet Slips hidden - feature not complete
  // Models hidden - feature not complete
  // Newsletter hidden for now
  // Analytics moved to /admin route (hidden from nav)
];


function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, currentUser, userProfile } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  return (
    <div className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 glass-card border-r">
      <div className="flex flex-col flex-grow pt-5 pb-4 overflow-y-auto scrollbar-thin">
        {/* Logo */}
        <div className="flex items-center flex-shrink-0 px-4">
          <h1 className="text-3xl font-black text-gradient">UKnew</h1>
        </div>

        {/* Navigation */}
        <nav className="mt-8 flex-1 px-2 space-y-1">
          {navigation.map((item) => {
            if (item.children) {
              return (
                <Disclosure as="div" key={item.name} className="space-y-1">
                  {({ open }) => (
                    <>
                      <Disclosure.Button
                        className={classNames(
                          'group w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg',
                          'hover:bg-bg-card-hover transition-all duration-200'
                        )}
                      >
                        <div className="flex items-center">
                          <item.icon
                            className="mr-3 h-6 w-6 text-text-secondary group-hover:text-accent-blue"
                            aria-hidden="true"
                          />
                          <span className="text-text-primary">{item.name}</span>
                        </div>
                        <ChevronDownIcon
                          className={classNames(
                            'h-5 w-5 text-text-secondary transition-transform duration-200',
                            open ? 'transform rotate-180' : ''
                          )}
                        />
                      </Disclosure.Button>
                      <Disclosure.Panel className="space-y-1 pl-11">
                        {item.children.map((subItem) => {
                          const isActive = location.pathname === subItem.href;
                          return (
                            <Link
                              key={subItem.name}
                              to={subItem.href}
                              className={classNames(
                                'block px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200',
                                isActive
                                  ? 'bg-accent-blue/10 text-accent-blue border-l-4 border-accent-blue'
                                  : 'text-text-secondary hover:bg-bg-card-hover hover:text-text-primary'
                              )}
                            >
                              {subItem.name}
                            </Link>
                          );
                        })}
                      </Disclosure.Panel>
                    </>
                  )}
                </Disclosure>
              );
            }

            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={classNames(
                  'group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200',
                  isActive
                    ? 'bg-accent-blue/10 text-accent-blue border-l-4 border-accent-blue'
                    : 'text-text-primary hover:bg-bg-card-hover'
                )}
              >
                <item.icon
                  className={classNames(
                    'mr-3 h-6 w-6',
                    isActive
                      ? 'text-accent-blue'
                      : 'text-text-secondary group-hover:text-accent-blue'
                  )}
                  aria-hidden="true"
                />
                <span className="flex-1">{item.name}</span>
                {item.premium && (
                  <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded">
                    PRO
                  </span>
                )}
                {item.badge && (
                  <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-gradient-to-r from-accent-blue to-accent-purple text-white rounded animate-pulse">
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Contact Us */}
        <div className="flex-shrink-0 px-2 pb-2">
          <a
            href="mailto:support@uknew.net"
            className="group flex items-center px-3 py-2 text-sm font-medium rounded-lg text-text-secondary hover:bg-bg-card-hover hover:text-text-primary transition-all duration-200"
          >
            <EnvelopeIcon className="mr-3 h-5 w-5 text-text-secondary group-hover:text-accent-blue" />
            Contact Us
          </a>
        </div>

        {/* Profile Dropdown */}
        <div className="flex-shrink-0 px-2 py-4 border-t border-border">
          <Menu as="div" className="relative">
            <Menu.Button className="w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg hover:bg-bg-card-hover transition-all duration-200">
              <UserCircleIcon className="h-8 w-8 text-text-secondary mr-3" />
              <div className="flex-1 text-left">
                <p className="text-text-primary font-medium truncate">
                  {userProfile?.display_name || currentUser?.email?.split('@')[0] || 'User'}
                </p>
                <p className="text-xs text-text-secondary truncate">
                  {currentUser?.email}
                </p>
              </div>
              <ChevronDownIcon className="h-5 w-5 text-text-secondary ml-2" />
            </Menu.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute bottom-full left-2 right-2 mb-2 bg-bg-card border border-border rounded-lg shadow-lg py-1 focus:outline-none">
                <Menu.Item>
                  {({ active }) => (
                    <Link
                      to="/account"
                      className={classNames(
                        'w-full flex items-center px-4 py-2 text-sm transition-colors',
                        active ? 'bg-bg-card-hover text-text-primary' : 'text-text-secondary'
                      )}
                    >
                      <Cog6ToothIcon className="h-5 w-5 mr-3" />
                      Account Settings
                    </Link>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={handleLogout}
                      className={classNames(
                        'w-full flex items-center px-4 py-2 text-sm transition-colors',
                        active ? 'bg-bg-card-hover text-text-primary' : 'text-text-secondary'
                      )}
                    >
                      <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3" />
                      Logout
                    </button>
                  )}
                </Menu.Item>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>
    </div>
  );
}
